// src/components/Calendar.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useTheme } from "@mui/material/styles";
import { getToken, base_url } from "../../config/config";

// const benutzerList = [
//   "Micheal Pastrick",
//   "Michael Otte",
//   "Daniel Herzog",
//   "Ronald Hasche",
//   "Rico Rasche",
//   "Jens Schubert",
//   "Jörg Melms",
//   "Maik Heinig",
//   "Marino Uhlemann",
//   "Jörg Marschner",
//   "Daniel Reddel",
//   "Matthias Kotte",
//   "Franko Heinig",
//   "Daniel Jendrolek",
//   "Sabine Wießner",
//   "Timur Serbey",
//   "Abdul Wasay",
// ].sort();

const CreatorList = [
  "Micheal Pastrick",
  "Michael Otte",
  "Daniel Herzog",
  "Daniel Jendrolek",
  "Sabine Wießner",
  "Timur Serbey",
  "Abdul Wasay",
].sort();

const EventsCalendar = () => {
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [newEvent, setNewEvent] = useState({
    creator: "",
    description: "",
    involvedPersons: [],
    type: "positive",
  });
  const [creatorSearchTerm, setCreatorSearchTerm] = useState("");
  const [involvedSearchTerm, setInvolvedSearchTerm] = useState("");
  const [showCreatorDropdown, setShowCreatorDropdown] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [benutzerList, setBenutzerList] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${base_url}/v1/getAllEmployees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data && Array.isArray(response.data)) {
          setBenutzerList(response.data);
        } else {
          console.error("Unexpected employee data format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployees();
  }, []);

  const themeClasses = {
    container: isDarkMode
      ? "bg-gray-900 text-gray-100"
      : "bg-white text-gray-900",
    calendar: isDarkMode ? "border-gray-700" : "border-gray-200",
    button: isDarkMode
      ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
      : "bg-gray-100 hover:bg-gray-200 text-gray-900",
    cell: {
      current: isDarkMode ? "bg-gray-800" : "bg-white",
      other: isDarkMode ? "bg-gray-900" : "bg-gray-50",
      hover: isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100",
    },
    modal: {
      bg: isDarkMode ? "bg-gray-800" : "bg-white",
      text: isDarkMode ? "text-gray-100" : "text-gray-900",
      input: isDarkMode
        ? "bg-gray-700 border-gray-600"
        : "bg-white border-gray-300",
    },
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getAllPandNEvents`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      const prevDate = new Date(year, month, -i);
      days.unshift(prevDate);
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setIsModalOpen(true);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${base_url}/v1/createPandNEvent`, {
        ...newEvent,
        involvedPersons: newEvent.involvedPersons.join(", "),
        date: selectedDate,
      });
      setIsModalOpen(false);
      setNewEvent({
        creator: "",
        description: "",
        involvedPersons: [],
        type: "positive",
      });
      fetchEvents();
    } catch (error) {
      console.error("Error creating event:", error);
    }
  };

  const handleEventDelete = async (eventId) => {
    try {
      await axios.delete(`${base_url}/v1/deletePandNEvent/${eventId}`);
      setShowEventPopup(false);
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const togglePerson = (person) => {
    setNewEvent((prev) => ({
      ...prev,
      involvedPersons: prev.involvedPersons.includes(person)
        ? prev.involvedPersons.filter((p) => p !== person)
        : [...prev.involvedPersons, person],
    }));
  };

  const selectCreator = (creator) => {
    setNewEvent((prev) => ({
      ...prev,
      creator,
    }));
    setShowCreatorDropdown(false);
  };

  const showEventDetails = (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setShowEventPopup(true);
  };

  const filteredCreators = CreatorList.filter((user) =>
    user.toLowerCase().includes(creatorSearchTerm.toLowerCase())
  );

  const filteredInvolved = benutzerList.filter((user) =>
    user.name.toLowerCase().includes(involvedSearchTerm.toLowerCase())
  );

  const getDayEvents = (day) => {
    return events.filter(
      (event) => new Date(event.date).toDateString() === day.toDateString()
    );
  };

  const formatInvolvedPersons = (involvedStr) => {
    if (!involvedStr) return "";
    const persons = involvedStr.split(", ");
    if (persons.length <= 2) return involvedStr;
    return `${persons[0]}, ${persons[1]} +${persons.length - 2}`;
  };

  return (
    <div className={`p-4 max-w-4xl mx-auto ${themeClasses.container}`}>
      <div className="mb-4 flex justify-between items-center">
        <button
          className={`px-4 py-2 rounded ${themeClasses.button}`}
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
            )
          }
        >
          Zurück
        </button>
        <h2 className="text-xl font-bold">
          {format(currentDate, "MMMM yyyy", { locale: de })}
        </h2>
        <button
          className={`px-4 py-2 rounded ${themeClasses.button}`}
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
            )
          }
        >
          Weiter
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"].map((day) => (
          <div key={day} className="text-center py-2 font-bold">
            {day}
          </div>
        ))}
        {getDaysInMonth(currentDate).map((day, index) => {
          const dayEvents = getDayEvents(day);
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();

          return (
            <div
              key={index}
              className={`border ${themeClasses.calendar} p-2 min-h-24 ${
                isCurrentMonth
                  ? themeClasses.cell.current
                  : themeClasses.cell.other
              } cursor-pointer relative ${themeClasses.cell.hover}`}
              onClick={() => handleDateClick(day)}
            >
              <div className="text-right">{format(day, "d")}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {dayEvents.map((event, eventIndex) => (
                  <div
                    key={eventIndex}
                    className={`w-full flex items-center gap-1 mb-1 ${
                      event.type === "positive"
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                    onMouseEnter={() => setHoveredEvent(event)}
                    onMouseLeave={() => setHoveredEvent(null)}
                    onClick={(e) => showEventDetails(event, e)}
                  >
                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        event.type === "positive"
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />
                    <div className="text-xs truncate flex-1">
                      {event.creator}: {event.description.substring(0, 15)}
                      {event.description.length > 15 ? "..." : ""}
                    </div>

                    {hoveredEvent === event && (
                      <div className="absolute z-20 border shadow-lg p-2 rounded w-60 text-sm left-full ml-2">
                        <div className="font-bold">{event.creator}</div>
                        <div className="my-1">{event.description}</div>
                        <div className="text-xs">
                          {formatInvolvedPersons(event.involvedPersons)}
                        </div>
                        <div className="text-xs mt-1">
                          {format(new Date(event.date), "dd.MM.yyyy")}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Event Details Popup */}
      {showEventPopup && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-30">
          <div
            className={`p-6 rounded-lg w-96 max-w-full ${themeClasses.modal.bg} ${themeClasses.modal.text}`}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Event Details</h2>
              <button
                onClick={() => setShowEventPopup(false)}
                className="hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium">Ersteller</div>
                <div>{selectedEvent.creator}</div>
              </div>

              <div>
                <div className="text-sm font-medium">Beschreibung</div>
                <div>{selectedEvent.description}</div>
              </div>

              <div>
                <div className="text-sm font-medium">Beteiligte Personen</div>
                <div>{selectedEvent.involvedPersons}</div>
              </div>

              <div>
                <div className="text-sm font-medium">Datum</div>
                <div>{format(new Date(selectedEvent.date), "dd.MM.yyyy")}</div>
              </div>

              <div>
                <div className="text-sm font-medium">Typ</div>
                <div
                  className={
                    selectedEvent.type === "positive"
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  {selectedEvent.type === "positive" ? "Positiv" : "Negativ"}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => handleEventDelete(selectedEvent._id)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Event Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-30">
          <div
            className={`p-6 rounded-lg w-96 ${themeClasses.modal.bg} ${themeClasses.modal.text}`}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Neues Event hinzufügen</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEventSubmit} className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium mb-1">
                  Ersteller
                </label>
                <div className="flex">
                  <input
                    type="text"
                    placeholder="Ersteller"
                    className={`w-full p-2 rounded-l ${themeClasses.modal.input}`}
                    value={newEvent.creator}
                    onChange={(e) =>
                      setNewEvent({ ...newEvent, creator: e.target.value })
                    }
                    onFocus={() => setShowCreatorDropdown(true)}
                  />
                  <button
                    type="button"
                    className="px-3 rounded-r border-y border-r"
                    onClick={() => setShowCreatorDropdown(!showCreatorDropdown)}
                  >
                    ▼
                  </button>
                </div>

                {showCreatorDropdown && (
                  <div className="absolute z-10 w-full mt-1 rounded shadow-lg">
                    <div className="p-2 border-b">
                      <input
                        type="text"
                        placeholder="Ersteller suchen..."
                        className={`w-full p-1 rounded ${themeClasses.modal.input}`}
                        value={creatorSearchTerm}
                        onChange={(e) => setCreatorSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {filteredCreators.map((person) => (
                        <div
                          key={person}
                          className="p-2 cursor-pointer hover:bg-gray-600"
                          onClick={() => selectCreator(person)}
                        >
                          {person}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Beschreibung
                </label>
                <textarea
                  placeholder="Beschreibung"
                  className={`w-full p-2 rounded ${themeClasses.modal.input}`}
                  value={newEvent.description}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, description: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium mb-1">
                  Beteiligte Personen
                </label>
                <input
                  type="text"
                  placeholder="Personen suchen..."
                  className={`w-full p-2 rounded ${themeClasses.modal.input}`}
                  value={involvedSearchTerm}
                  onChange={(e) => setInvolvedSearchTerm(e.target.value)}
                />
                <div className="max-h-40 overflow-y-auto border rounded">
                  {filteredInvolved.map((person) => (
                    <div
                      key={person.name}
                      className={`p-2 cursor-pointer ${
                        newEvent.involvedPersons.includes(person.name)
                          ? "bg-blue-900"
                          : ""
                      }`}
                      onClick={() => togglePerson(person.name)}
                    >
                      <input
                        type="checkbox"
                        checked={newEvent.involvedPersons.includes(person.name)}
                        onChange={() => togglePerson(person.name)}
                        className="mr-2"
                      />
                      <span>{person.name}</span>
                    </div>
                  ))}
                </div>
                <div className="text-sm">
                  Ausgewählt: {newEvent.involvedPersons.length} Personen
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Event-Typ
                </label>
                <select
                  className={`w-full p-2 rounded ${themeClasses.modal.input}`}
                  value={newEvent.type}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, type: e.target.value })
                  }
                >
                  <option value="positive">Positiv</option>
                  <option value="negative">Negativ</option>
                </select>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className={`px-4 py-2 rounded ${themeClasses.button}`}
                  onClick={() => setIsModalOpen(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsCalendar;
